/**
 * Email validation utilities for the VotesNow app
 */

/**
 * Validates if an email address is properly formatted
 * @param email - The email address to validate
 * @returns boolean indicating if the email is valid
 */
export function isValidEmail(email: string): boolean {
  if (!email || typeof email !== 'string') {
    return false;
  }

  // Trim whitespace
  const trimmedEmail = email.trim();
  
  // Basic length check
  if (trimmedEmail.length < 5 || trimmedEmail.length > 254) {
    return false;
  }

  // RFC 5322 compliant email regex (simplified version)
  const emailRegex = /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*$/;
  
  return emailRegex.test(trimmedEmail);
}

/**
 * Gets a user-friendly error message for invalid email addresses
 * @param email - The email address to validate
 * @returns Error message string or null if email is valid
 */
export function getEmailErrorMessage(email: string): string | null {
  if (!email || typeof email !== 'string') {
    return 'Email is required';
  }

  const trimmedEmail = email.trim();
  
  if (trimmedEmail.length === 0) {
    return 'Email is required';
  }

  if (trimmedEmail.length < 5) {
    return 'Email is too short';
  }

  if (trimmedEmail.length > 254) {
    return 'Email is too long';
  }

  if (!trimmedEmail.includes('@')) {
    return 'Email must contain @ symbol';
  }

  if (trimmedEmail.startsWith('@') || trimmedEmail.endsWith('@')) {
    return 'Email cannot start or end with @';
  }

  if (trimmedEmail.includes('..')) {
    return 'Email cannot contain consecutive dots';
  }

  if (!isValidEmail(trimmedEmail)) {
    return 'Please enter a valid email address';
  }

  return null;
}

/**
 * Normalizes an email address by trimming whitespace and converting to lowercase
 * @param email - The email address to normalize
 * @returns Normalized email address
 */
export function normalizeEmail(email: string): string {
  if (!email || typeof email !== 'string') {
    return '';
  }
  
  return email.trim().toLowerCase();
}
